import { Information } from '@/DTOS/CompanyInformation/Information';
import { useHandleResponse } from '@/utils/useHandleResponse';
import { useMutation } from '@tanstack/react-query';
import { App, AutoComplete, Button, Col, Divider, Form, Input, Row, Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateField, updateProps } from '../../../contextRedux/AdministrationCompany/AdministrationCompanyModel';
import AdministrationCompanyService from '../../../services/administrationCompany/AdministrationCompanyService';
import CompanyInformationService from '../../../services/companyInformation/CompanyInformationService';
import { RootState } from '../../../store';
import './AdministrationCompanyForm.css';

const { Option } = Select;

const AdministrationCompanyForm: FC<any> = () => {
  const [form] = useForm();
  const dispatch = useDispatch();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();

  const administrationCompany = useSelector((state: RootState) => state.models.administrationCompany);

  const initialValues = {
    id: useSelector((state: RootState) => state?.models?.company?.id),
    type: useSelector((state: RootState) => state?.models?.company?.type),
    name: useSelector((state: RootState) => state?.models?.company?.name),
    country: useSelector((state: RootState) => state?.models?.company?.country),
    locality: useSelector((state: RootState) => state?.models?.company?.locality),
    address: useSelector((state: RootState) => state?.models?.company?.address),
    phone: useSelector((state: RootState) => state?.models?.company?.phone),
    email: useSelector((state: RootState) => state?.models?.company?.email),
    bankAccount: useSelector((state: RootState) => state?.models?.company?.bankAccount),
    bank: useSelector((state: RootState) => state?.models?.company?.bank),
    cnp: useSelector((state: RootState) => state?.models?.company?.cnp),
    signature: useSelector((state: RootState) => state?.models?.company?.signature),
    cui: useSelector((state: RootState) => state?.models?.company?.cui),
    registrationNumber: useSelector((state: RootState) => state?.models?.company?.registrationNumber),
    activityDescription: useSelector((state: RootState) => state?.models?.company?.activityDescription),
    leadership: useSelector((state: RootState) => state?.models?.company?.leadership),
    webAddress: useSelector((state: RootState) => state?.models?.company?.webAddress),
  };

  const handleCUIChange = (value: string) => {
    const specification: Information = {
      cui: value,
    };
    CompanyInformationService.informations(specification)
      .then((data: any) => {
        if (data.length === 1) {
          const fieldsValue = {
            ...form.getFieldsValue(true),
            name: data[0]?.name,
            address: data[0]?.fullAddress,
            registrationNumber: data[0]?.registrationCode,
          };

          form.setFieldsValue(fieldsValue);
          dispatch(updateField({ field: 'name', value: data[0]?.name }));
          dispatch(updateField({ field: 'address', value: data[0]?.fullAddress }));
          dispatch(
            updateField({
              field: 'registrationNumber',
              value: data[0]?.registrationCode,
            })
          );
        } else {
          const fieldsValue = {
            ...form.getFieldsValue(true),
            name: null,
            address: null,
            registrationNumber: null,
          };
          form.setFieldsValue(fieldsValue);
          dispatch(updateField({ field: 'name', value: null }));
          dispatch(updateField({ field: 'address', value: null }));
          dispatch(
            updateField({
              field: 'registrationNumber',
              value: null,
            })
          );
        }
      })
      .catch(() => {
        const fieldsValue = {
          ...form.getFieldsValue(true),
          name: null,
          address: null,
          registrationNumber: null,
        };
        form.setFieldsValue(fieldsValue);
        dispatch(updateField({ field: 'name', value: null }));
        dispatch(updateField({ field: 'address', value: null }));
        dispatch(
          updateField({
            field: 'registrationNumber',
            value: null,
          })
        );
      });
  };

  const handleBankAccountChange = (value: string) => {
    const specification: Information = {
      iban: value,
    };
    CompanyInformationService.bank(specification)
      .then((data: any) => {
        const fieldsValue = {
          ...form.getFieldsValue(true),
          bank: data?.bank_name,
        };
        form.setFieldsValue(fieldsValue);
        dispatch(updateField({ field: 'bank', value: data?.bank_name }));
      })
      .catch(() => {
        const fieldsValue = { ...form.getFieldsValue(true), bank: null };
        form.setFieldsValue(fieldsValue);
        dispatch(updateField({ field: 'bank', value: null }));
      });
  };

  const onSaveCompany = async () => {
    saveCompany(administrationCompany);
  };

  const { mutate: saveCompany, isPending: _isSavingCompany } = useMutation({
    mutationFn: (formData: any) => AdministrationCompanyService.upsert(administrationCompany.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, data?.response?.name);
        dispatch(updateProps({ field: 'isModalVisible', value: false }));
        dispatch(updateProps({ field: 'refresh', value: !administrationCompany.props.refresh }));
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  const handleFilter = (inputValue: string, option: any) => {
    return option?.value?.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  };

  const handleFieldChange = (changedValues: any, allValues: any) => {
    for (const key in changedValues) {
      if (key !== 'contactPersons') {
        dispatch(updateField({ field: key, value: changedValues[key] }));
      }
    }
    if (changedValues.contactPersons) {
      dispatch(updateField({ field: 'contactPersons', value: allValues.contactPersons }));
    }
  };

  useEffect(() => {
    form.setFieldsValue(administrationCompany);
  }, [administrationCompany]);

  return (
    <Form
      form={form}
      name='administrationCompanyForm'
      initialValues={initialValues}
      onValuesChange={handleFieldChange}
      onFinish={onSaveCompany}
      layout='vertical'
      className='administrationCompanyForm'
    >
      <Divider />
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label='Tip' name='type' rules={[{ required: true, message: 'Introdu tipul!' }]}>
            <AutoComplete filterOption={handleFilter}>
              {['Persoana Fizica', 'Persoana Juridica'].map((typeItem, index) => (
                <Option key={index} value={typeItem}>
                  {typeItem}
                </Option>
              ))}
            </AutoComplete>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label='Denumire' name='name' rules={[{ required: true, message: 'Introdu denumire!' }]}>
            <Input />
          </Form.Item>
        </Col>

        {administrationCompany?.type === 'Persoana Fizica' && (
          <Col xs={24} sm={12} md={8}>
            <Form.Item label='CNP' name='cnp' rules={[{ required: true, message: 'Introdu CNP!' }]}>
              <Input type='number' />
            </Form.Item>
          </Col>
        )}

        {administrationCompany?.type === 'Persoana Juridica' && (
          <>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label='CUI' name='cui' rules={[{ required: true, message: 'Introdu CUI!' }]}>
                <Input onChange={(e) => handleCUIChange(e.target.value)} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label='Numar inregistrare'
                name='registrationNumber'
                rules={[
                  {
                    required: true,
                    message: 'Introdu descriere activitate!',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </>
        )}

        <Col xs={24} sm={12} md={8}>
          <Form.Item label='Adresa' name='address' rules={[{ required: true, message: 'Introdu address!' }]}>
            <Input />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item label='IBAN' name='bankAccount'>
            <Input onChange={(e) => handleBankAccountChange(e.target.value)} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label='Banca' name='bank'>
            <Input />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item label='Telefon' name='phone' rules={[{ required: true, message: 'Introdu telefon!' }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label='Email' name='email' rules={[{ required: true, message: 'Introdu email!' }]}>
            <Input />
          </Form.Item>
        </Col>
        {administrationCompany?.type === 'Persoana Juridica' && (
          <>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Persoana contact' name='leadership'>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Descriere activitate' name='activityDescription'>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Adresa web' name='webAddress'>
                <Input />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
      <Form.Item>
        <Button type='primary' htmlType='submit' className='form-item-btn-finalize'>
          Finalizare
        </Button>
      </Form.Item>
    </Form>
  );
};

export default AdministrationCompanyForm;
